import React, { FC, useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';

import { useSpots } from '../../../lib/spots';
import { getSpotPermissions } from 'lib/spot-permissions';
import { getPermissions } from 'lib/permissions';
import { UserPage } from 'components/UserPage';
import { Section } from 'components/Section';
import { BreadCrumbs } from 'components/BreadCrumbs';


type Props = {};

type Staff = {
    id: number;
    name: string;
    role: string;
    userId: string;
    facebookId: string;
    shortId: string;
    public: boolean;
    icon_path: string;
};


export const SpotStaffPage: FC<Props> = (props) => {
    // @ts-ignore
    const id = props.match.params.id;
    const { get } = useSpots();
    const [spot, setSpot] = useState<Spot | null>(null);
    const [staffList, setStaffList] = useState<Staff[]>([]);
    const [role, setRole] = useState<string | null>();
    const [loading, setLoading] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
    const [selectedRole, setSelectedRole] = useState<string | null>();
    const history = useHistory();

    useEffect(() => {
        async function initialize() {
            const spotPermissions = await getSpotPermissions(id);
            const userPermission = await getPermissions();
            if (!spotPermissions.view) {
                if (userPermission.status === 'unpaid') {
                    history.push(
                        `/map/spot/${id}`,
                        {
                            flash: 'サービスをご利用になるには未払い金をお支払いください。',
                            error: true,
                        },
                    );
                }
                return;
            }
            const { data } = await axios.get(`${window.location.origin}/api/map/spots/${id}/staff`);
            if (!data.staffList || data.staffList.length <= 0) {
                window.location.href = `/map/spot/${id}`;
                return;
            };
            const spot = await get(id);
            setSpot(spot);
            setRole(data.role ?? null);
            setStaffList(data.staffList ?? []);
        };
        initialize();
    }, []);

    async function approveRequest(clerkId: number) {
        if (loading) return;
        try {
            setLoading(true);
            const { data: { success, redirect } } = await axios.post(
                `${window.location.origin}/api/map/spots/${id}/staff/approve`,
                { clerkId },
            );
            if (success) {
                window.location.reload();
            } else {
                window.location.href = redirect ?? '/map';
            };
        } catch (e) {
            window.alert('リクエストの承認ができませんでした。');
        } finally {
            setLoading(false);
        };
    };

    async function rejectRequest(clerkId: number) {
        if (loading) return;
        try {
            setLoading(true);
            const { data: { success, redirect } } = await axios.post(
                `${window.location.origin}/api/map/spots/${id}/staff/reject`,
                { clerkId },
            );
            if (success) {
                window.location.reload();
            } else {
                window.location.href = redirect ?? '/map';
            };
        } catch (e) {
            window.alert('リクエストを却下できませんでした。');
        } finally {
            setLoading(false);
        };
    };

    function onEditButtonClick(staff: Staff) {
        setSelectedStaff(staff);
        setIsEditModalOpen(true);
    };

    async function editStaffRole(staff: Staff, role: string) {
        if (loading) return;
        try {
            const { data: { success, redirect } } = await axios.post(
                `${window.location.origin}/api/map/spots/${id}/staff/edit`,
                {
                    clerkId: staff.id,
                    role: role === "管理者" ? 1 : 2,
                },
            );
            if (success) {
                window.location.reload();
            } else {
                window.location.href = redirect ?? '/map';
            };
        } catch (e) {
            window.alert('リクエストを却下できませんでした。');
        } finally {
            setLoading(false);
        };
    };

    async function deleteStaff(staff: Staff) {
        if (loading) return;
        try {
            const { data: { success, redirect } } = await axios.post(
                `${window.location.origin}/api/map/spots/${id}/staff/remove`,
                { clerkId: staff.id },
            );
            if (success) {
                window.location.reload();
            } else {
                window.location.href = redirect ?? '/map';
            };
        } catch (e) {
            window.alert('リクエストを却下できませんでした。');
        } finally {
            setLoading(false);
        };
    };

    function onDeleteButtonClick(staff: Staff) {
        setSelectedStaff(staff);
        setIsDeleteModalOpen(true);
    };

    return (
        <UserPage title="Salon.jp | スタッフ管理" disableBackButton>
            {(isDeleteModalOpen && selectedStaff) && (
                <div className="modal-mask" onClick={() => setIsDeleteModalOpen(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <p>スタッフリストから削除しますか？</p>
                        <div className="delete-actions">
                            <button className="cancel-button" onClick={() => setIsDeleteModalOpen(false)}>キャンセル</button>
                            <button className="delete-button" onClick={() => deleteStaff(selectedStaff)}>削除</button>
                        </div>
                    </div>
                </div>
            )}
            {(isEditModalOpen && selectedStaff) && (
                <div className="modal-mask" onClick={() => setIsEditModalOpen(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="roles-container">
                            <select className="roles" name="roles" id="roles" onChange={(e) => setSelectedRole(e.target.value)}>
                                <option value="管理者" selected={selectedStaff.role === "管理者"}>管理者</option>
                                <option value="スタッフ" selected={selectedStaff.role === "スタッフ"}>スタッフ</option>
                            </select>
                        </div>
                        <div className="edit-actions">
                            <button className="cancel-button" onClick={() => setIsEditModalOpen(false)}>キャンセル</button>
                            {(selectedRole && selectedRole !== selectedStaff.role)
                                ? <button className="confirm-button" onClick={() => editStaffRole(selectedStaff, selectedRole)}>変更</button>
                                : <button className="confirm-button disabled">変更</button>}
                        </div>
                    </div>
                </div>
            )}
            <Section fillOnSp>
                {spot && (
                    <div className="bread-crumbs-wrapper">
                        <BreadCrumbs items={[
                            {
                                title: 'お店の一覧',
                                href: '/map/my_list',
                            },
                            {
                                title: spot.name,
                                href: `/map/spot/${id}`,
                            },
                            {
                                title: 'お店管理',
                                href: `/map/spot/${id}/admin`,
                            },
                            {
                                title: 'スタッフ管理',
                                href: `/map/spot/${id}/staff`,
                            },
                        ]} />
                    </div>
                )}
                <div className="wrap">
                    <div className="head">
                        <img src="/img/map_admin/staff.png" />
                        <h1 className="title">スタッフ管理</h1>
                    </div>
                    <div className="list staff">
                        {staffList.filter((staff) => staff.role === "管理者").map((staff) => (
                            <li className="item" key={staff.id}>
                                {staff.public
                                    ? (
                                        <a className="staff-info" href={`${window.location.origin}/members/view/${staff.shortId ?? ""}`}>
                                            <div className="staff-image">
                                                <img className="staff-image" width={60} height={60} src={staff.facebookId ? `/user_icons/get/dummy?user_id=${staff.userId}` : `${staff.icon_path}`} />
                                            </div>
                                            <div className="staff-name">{staff.name}</div>
                                        </a>
                                    ) : (
                                        <div className="staff-info">
                                            <div className="staff-image">
                                                <img className="staff-image" width={60} height={60} src={staff.facebookId ? `/user_icons/get/dummy?user_id=${staff.userId}` : `${staff.icon_path}`} />
                                            </div>
                                            <div className="staff-name">{staff.name}</div>
                                        </div>
                                    )}
                                <div className="staff-actions">
                                    <div className="staff-role">{staff.role}</div>
                                    {(role === "管理者" && staffList.length > 1) && (
                                        <>
                                            <button
                                                className="edit-button"
                                                onClick={() => onEditButtonClick(staff)}
                                            >
                                                <img src="/img/btn_edit.png" />
                                            </button>
                                            <button
                                                className="delete-button"
                                                onClick={() => onDeleteButtonClick(staff)}
                                            >
                                                <img src="/img/trash.png" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </li>
                        ))}
                        {staffList.filter((staff) => staff.role === "スタッフ").map((staff) => (
                            <li className="item" key={staff.id}>
                                {staff.public
                                    ? (
                                        <a className="staff-info" href={`${window.location.origin}/members/view/${staff.shortId ?? ""}`}>
                                            <div className="staff-image">
                                                <img className="staff-image" width={60} height={60} src={staff.facebookId ? `/user_icons/get/dummy?user_id=${staff.userId}` : `${staff.icon_path}`} />
                                            </div>
                                            <div className="staff-name">{staff.name}</div>
                                        </a>
                                    ) : (
                                        <div className="staff-info">
                                            <div className="staff-image">
                                                <img className="staff-image" width={60} height={60} src={staff.facebookId ? `/user_icons/get/dummy?user_id=${staff.userId}` : `${staff.icon_path}`} />
                                            </div>
                                            <div className="staff-name">{staff.name}</div>
                                        </div>
                                    )}
                                <div className="staff-actions">
                                    <div className="staff-role">{staff.role}</div>
                                    {staffList.every((staff) => staff.role === "スタッフ") && (
                                        <button
                                            className="edit-button"
                                            onClick={() => onEditButtonClick(staff)}
                                        >
                                            <img src="/img/btn_edit.png" />
                                        </button>
                                    )}
                                    {(role === "管理者" && staffList.length > 1) && (
                                        <>
                                            <button
                                                className="edit-button"
                                                onClick={() => onEditButtonClick(staff)}
                                            >
                                                <img src="/img/btn_edit.png" />
                                            </button>
                                            <button
                                                className="delete-button"
                                                onClick={() => onDeleteButtonClick(staff)}
                                            >
                                                <img src="/img/trash.png" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </li>
                        ))}
                    </div>
                    {staffList.filter((staff) => staff.role === "申請中").length !== 0 && (
                        <>
                            <div className="applicant-head">承認待ち</div>
                            <div className="list applicant">
                                {staffList.filter((staff) => staff.role === "申請中").map((staff) => (
                                    <li className="item" key={staff.id}>
                                        {staff.public
                                            ? (
                                                <a className="staff-info" href={`${window.location.origin}/members/view/${staff.shortId ?? ""}`}>
                                                    <div className="staff-image">
                                                        <img className="staff-image" width={60} height={60} src={staff.facebookId ? `/user_icons/get/dummy?user_id=${staff.userId}` : `${staff.icon_path}`} />
                                                    </div>
                                                    <div className="staff-name">{staff.name}</div>
                                                </a>
                                            ) : (
                                                <div className="staff-info">
                                                    <div className="staff-image">
                                                        <img className="staff-image" width={60} height={60} src={staff.facebookId ? `/user_icons/get/dummy?user_id=${staff.userId}` : `${staff.icon_path}`} />
                                                    </div>
                                                    <div className="staff-name">{staff.name}</div>
                                                </div>
                                            )}
                                        {role === "管理者" && (
                                            <div className="applicant-actions">
                                                <button
                                                    className="reject-button"
                                                    onClick={() => rejectRequest(staff.id)}
                                                >
                                                    却下
                                                </button>
                                                <button
                                                    className="approve-button"
                                                    onClick={() => approveRequest(staff.id)}
                                                >
                                                    承認
                                                </button>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </div>
                        </>
                    )}
                    {(staffList.every((staff) => staff.role === "スタッフ") && staffList.length !== 0) && (
                        <div className="notification">
                            <div className="notification-title">
                                <h2>店舗管理者が登録されていません</h2>
                            </div>
                            <span className="notification-message">
                                <img src="/img/btn_edit.png" />から権限を店舗管理者に変更することで、店舗情報の編集・スタッフ管理を行うことができます。
                            </span>
                        </div>
                    )}
                </div>
            </Section>

            <style jsx>{`
                .modal-mask {
                    position: fixed;
                    top: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(0, 0, 0, 0.5);
                    z-index: 10;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .modal-mask > .modal {
                    border-radius: 20px;
                    min-width: 300px;
                    max-width: 80%;
                    height: 200px;
                    background: #fff;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex-direction: column;
                }
                .modal > p {
                    margin-bottom: 30px;
                }
                .delete-actions {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0 20px 0 20px;
                    width: 100%;
                }
                .delete-actions > .cancel-button {
                    background: none;
                    border: none;
                    color: #bbb;
                    width: 50%;
                }
                .delete-actions > .delete-button {
                    border: none;
                    background: red;
                    padding: 3px 15px 3px 15px;
                    color: #fff;
                    border-radius: 4px;
                    width: 50%;
                }
                .roles-container {
                    width: 100%;
                    padding: 0 20px 0 20px;
                    display: flex;
                    justify-content: center;
                }
                .roles {
                    width: 50%;
                    margin-bottom: 20px;
                    padding: 4px 48px 4px 4px;
                }
                .edit-actions {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0 20px 0 20px;
                    width: 100%;
                }
                .edit-actions > .confirm-button {
                    border: none;
                    background: rgb(253, 134, 44);
                    padding: 3px 15px 3px 15px;
                    color: #fff;
                    border-radius: 4px;
                    width: 50%;
                }
                .edit-actions >  .confirm-button.disabled {
                    background: #bbb;
                }
                .edit-actions > .cancel-button {
                    background: none;
                    border: none;
                    color: #bbb;
                    width: 50%;
                }
                .bread-crumbs-wrapper {
                    padding-top: 10px;
                    padding-bottom: 20px;
                }
                .wrap {
                    min-height: 50vh;
                    padding: 1rem;
                    padding-top: 0px;
                }
                .head {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-bottom: 2rem;
                }
                .head > .title {
                    font-size: 1rem;
                    padding-left: 0.5rem;
                }
                .list {
                    margin-left: 0;
                    padding-left: 0;
                    border-top: 2px solid #aaaaaa;
                    line-style: none;
                }
                .list > .item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 0.5rem 1rem 0;
                    border-bottom: 1px solid #a8a8a8;
                    padding-right: 10px;
                }
                .staff {
                    margin-bottom: 60px;
                }
                .applicant-head {
                    font-weight: bold;
                }
                .staff-info {
                    display: flex;
                    justify-content: start;
                    align-items: center;
                    text-decoration: none;
                }
                .staff-info > .staff-name {
                    color: rgb(237, 124, 79);
                    font-weight: bold;
                }
                .staff-info > .staff-image {
                    width: 60px;
                    height: 60px;
                    margin-right: 10px;
                }
                .staff-image > img {
                    border-radius: 50%;
                }
                .staff-actions {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .staff-actions > .staff-role {
                    margin-right: 10px;
                }
                .staff-actions > .edit-button,
                .staff-actions > .delete-button {
                    background: none;
                    border: none;
                }
                .staff-actions > .edit-button > img,
                .staff-actions > .delete-button > img {
                    width: 25px;
                    height: 25px;
                }
                .applicant-actions {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .applicant-actions > .reject-button {
                    border-radius: 4px;
                    border: 1px solid #bbb;
                    color: #bbb;
                    padding: 3px 10px 3px 10px;
                    margin-right: 10px;
                }
                .applicant-actions > .approve-button {
                    border-radius: 4px;
                    border: none;
                    background: rgb(237, 124, 79);
                    padding: 3px 10px 3px 10px;
                    color: #fff;
                }
                .notification {
                    padding: 30px 16px 30px 16px;
                    border: 1px solid rgb(253, 134, 44);
                    border-radius: 8px;
                }
                .notification-title {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 5px;
                }
                .notification-title > h2 {
                    font-size: 16px;
                    margin: 0;
                }
                .notification-message {
                    font-size: 14px;
                }
                .notification-message > img {
                    width: 14px;
                    height: 14px;
                }
                @media screen and (max-width: 540px) {
                    .wrap {
                        min-height: 100vh;
                    }
                    .staff-name {
                        width: calc(16px * 6.8);
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                    .notification-message {
                        font-size: 12px;
                    }
                    .notification-message > img {
                        width: 12px;
                        height: 12px;
                    }
                }
            `}</style>
        </UserPage>
    );
};
